import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, TrendingDown, TrendingUp, DollarSign, HeartPulse, ClipboardList, LineChart, AlertTriangle, Users2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

/**
 * /for/athletic-directors — the institutional purchase-decision page.
 *
 * The AD's job is not to coach — it is to protect scholarship value,
 * defend the medical line on the P&L, mitigate insurance exposure,
 * and keep the department competitive. This page frames BioHacker
 * Sports™ specifically for that P&L conversation: risk categories,
 * cost math, and the leverage a single load-monitoring platform
 * gives an AD across every sport in the portfolio.
 */
export default function ForAthleticDirectors() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="for-ad-page">
      <SiteNav />

      {/* Hero */}
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" /> For Athletic Directors
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]"
            data-testid="for-ad-headline">
          Every non-contact injury<br />
          <span className="text-white/50">is a P&L line item you could have prevented.</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-3xl leading-relaxed">
          BioHacker Sports™ is not a coaching product. It is <b className="text-white">institutional risk infrastructure</b>
          {" "}— the same physics-based load monitoring your professional counterparts already use, packaged for
          your department. This page frames the case in the terms that matter to your office: financial exposure,
          insurance liability, scholarship value protection, and cross-sport medical cost containment.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/request-demo" data-testid="for-ad-cta-demo">
            <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request an AD-scoped demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/enterprise/contact" data-testid="for-ad-cta-enterprise">
            <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Talk to enterprise sales
            </Button>
          </Link>
        </div>
      </section>

      {/* Risk categories */}
      <section className="py-16 max-w-6xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> Risk categories · department-wide
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight leading-[1.1]">
          The four financial exposures every AD carries.<br />
          <span className="text-white/50">And what BioHacker retires for you.</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="for-ad-risks">
          {[
            { icon: DollarSign, label: "Direct medical spend", tone: "#F59E0B",
              body:  "One ACL reconstruction on scholarship averages $40k–$50k medically, plus $8k–$15k in rehab, plus the roster hole itself. The average FBS program carries 3–7 season-ending non-contact injuries a year — the six-figure line item.",
              relief:"Cut non-contact soft-tissue attrition by managing load with the same physics the pros already use. Every prevented injury drops straight to the medical line." },
            { icon: ShieldCheck, label: "Insurance & liability exposure", tone: "#F43F5E",
              body:  "Institutional insurance premiums rise with claim history. Catastrophic injury (spinal, cardiac, cognitive) exposure sits with the department, and Title IX + student-welfare litigation is trending upward every year.",
              relief:"Auditable, dated workload + wellness records prove duty-of-care due-diligence. SOC 2 + HIPAA-aligned records reduce discoverable gaps." },
            { icon: GraduationCap, label: "Scholarship value erosion", tone: "#22D3EE",
              body:  "A blown-out scholarship athlete is a fully funded roster slot returning $0 in eligibility that year — plus lost NIL, portal risk, and diminished recruiting-class value for the following cycle.",
              relief:"Availability-first roster planning protects the assets that generate revenue. Portal-and-NIL calculus shifts when your top-3 stay healthy." },
            { icon: LineChart, label: "Competitive & reputational decay", tone: "#A78BFA",
              body:  "Late-season injury attrition costs conference standing, TV inventory, ticketing, donor confidence, and coach retention. Every game lost with your best players unavailable compounds a multi-year P&L drag.",
              relief:"Data-driven availability boards + cross-sport KPIs give the AD one number for institutional readiness, defensible to the president and the board." },
          ].map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.label} className="ink-card rounded-2xl p-6" data-testid={`for-ad-risk-${r.label.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${r.tone}22`, color: r.tone }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="font-display font-black text-white text-lg">{r.label}</div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-300/80 font-bold mb-1">The exposure</div>
                    <div className="text-white/80 leading-relaxed">{r.body}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-bold mb-1">What BioHacker retires</div>
                    <div className="text-white/80 leading-relaxed">{r.relief}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Cost math */}
      <section className="py-16 border-t border-white/8">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" /> The cost math
          </div>
          <h2 className="font-display font-black text-3xl text-white tracking-tight leading-[1.1]">
            One prevented ACL pays for BioHacker across every sport for three years.
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="for-ad-cost-math">
            {[
              { k: "Single ACL cost to the institution", v: "$50k+", note: "Surgery + rehab + lost season value" },
              { k: "Non-contact soft-tissue reduction target", v: "20–40%", note: "Peer-published range with load monitoring" },
              { k: "BioHacker department-wide annual cost", v: "< $30k", note: "Performance tier ships every engine, every sport" },
            ].map((row) => (
              <div key={row.k} className="ink-card rounded-2xl p-6 text-center">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold mb-2">{row.k}</div>
                <div className="font-display font-black text-4xl text-white tabular-nums" style={{ color: "var(--brand-secondary)" }}>{row.v}</div>
                <div className="mt-2 text-xs text-white/60 leading-snug">{row.note}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-white/60 text-sm max-w-3xl">
            The math is not aspirational. Load monitoring is <b className="text-white">standard of care</b> in every
            professional locker room in the country. What BioHacker Sports™ does is deliver that standard at
            department-scale pricing with cross-sport visibility no wearable vendor alone can provide.
          </p>
        </div>
      </section>

      {/* What the AD sees */}
      <section className="py-16 border-t border-white/8 max-w-6xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5" /> The AD dashboard
        </div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight leading-[1.1]">
          One executive board.<br />
          <span className="text-white/50">Every sport, every roster, every risk trend.</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="for-ad-surface">
          {[
            { icon: Users2,     label: "Cross-sport availability",   body: "Roster availability trended across every sport with one KPI — defensible to the president, the board, and the insurance carrier." },
            { icon: HeartPulse, label: "Injury registry + trend",    body: "Every event, cause, and RTP progression logged and dated. Duty-of-care evidence and a live view of program-wide medical trajectory." },
            { icon: TrendingDown,label: "Prevention ROI",            body: "Model the medical-line impact of load monitoring by sport: expected reduction, dollarized, tracked against actual claims history." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="ink-card rounded-2xl p-6">
                <Icon className="h-6 w-6 text-fuchsia-300 mb-3" />
                <div className="font-display font-black text-white text-lg">{c.label}</div>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-white/8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight">
            Ready to move medical spend from reactive to prevention?
          </h2>
          <p className="mt-3 text-white/60 max-w-2xl mx-auto">
            Book a scoped walkthrough. We'll model the injury economics against your specific sport portfolio.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/request-demo">
              <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request an AD-scoped demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/enterprise/contact">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                Talk to enterprise sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
